import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Search, 
  Filter,
  ChevronRight,
  ChevronDown,
  Check,
  X,
  AlertCircle,
  AlertTriangle,
  Shield,
  BookOpen,
  Wrench,
  Settings,
  Clock,
  RefreshCw,
  Download,
  BarChart3,
  PieChart,
  FileText,
  Database,
  Link2,
  Plus,
  Eye,
  Edit,
  Trash2,
  Copy,
  CheckCircle2,
  XCircle,
  Info,
  Tag,
  Layers,
  GitBranch
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * RuleCoverageDashboard - Admin UI for Canonical Rule Engine Management
 * 
 * Features:
 * - Coverage analysis (mapped vs unmapped VI items)
 * - Rule library browser
 * - Pathway rule management (EML, ABS, etc.)
 * - VI mapping editor
 * - Rule relationship visualization
 */

const RuleCoverageDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [rulesRes, coverageRes] = await Promise.all([
        axios.get(`${API}/rules`, { headers }),
        axios.get(`${API}/rules/coverage`, { headers })
      ]);

      setRules(rulesRes.data.rules || []);
      setMappings(rulesRes.data.vi_mappings || []);
      setCoverage(coverageRes.data);
    } catch (error) {
      console.error('Failed to fetch rule data:', error);
      toast.error('Failed to load rule coverage data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const stats = useMemo(() => {
    if (!rules.length) return null;

    const totalRules = rules.length;
    const activeRules = rules.filter(r => r.status === 'active').length;
    const pathwayRules = rules.filter(r => r.is_pathway_rule).length;
    const diagnosticRules = rules.filter(r => r.category === 'diagnostic').length;
    const brakeRules = rules.filter(r => r.category === 'brakes').length;
    const suspensionRules = rules.filter(r => r.category === 'suspension').length;

    const totalMappings = mappings.length;
    const uniqueVIItems = new Set(mappings.map(m => m.vi_item_key)).size;

    const coveragePercent = coverage?.coverage_percent || 0;

    return {
      totalRules,
      activeRules,
      pathwayRules,
      diagnosticRules,
      brakeRules,
      suspensionRules,
      totalMappings,
      uniqueVIItems,
      coveragePercent
    };
  }, [rules, mappings, coverage]);

  // Filter rules based on search and category
  const filteredRules = useMemo(() => {
    let result = rules;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(rule =>
        rule.canonical_name?.toLowerCase().includes(query) ||
        rule.rule_id?.toLowerCase().includes(query) ||
        rule.aliases?.some(a => a.toLowerCase().includes(query)) ||
        rule.job_group_key?.toLowerCase().includes(query)
      );
    }

    if (filterCategory !== 'all') {
      if (filterCategory === 'pathway') {
        result = result.filter(r => r.is_pathway_rule);
      } else {
        result = result.filter(r => r.category === filterCategory);
      }
    }

    return result;
  }, [rules, searchQuery, filterCategory]);

  // Group rules by category
  const groupedRules = useMemo(() => {
    const groups = {};
    filteredRules.forEach(rule => {
      const cat = rule.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(rule);
    });
    return groups;
  }, [filteredRules]);

  const categoryColors = {
    brakes: 'bg-red-100 text-red-800 border-red-200',
    suspension: 'bg-blue-100 text-blue-800 border-blue-200',
    diagnostic: 'bg-purple-100 text-purple-800 border-purple-200',
    service: 'bg-green-100 text-green-800 border-green-200',
    exhaust: 'bg-orange-100 text-orange-800 border-orange-200',
    steering: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    tyres: 'bg-slate-100 text-slate-800 border-slate-200',
    electrical: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    engine: 'bg-pink-100 text-pink-800 border-pink-200',
    cooling: 'bg-teal-100 text-teal-800 border-teal-200',
    other: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading rule coverage data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="rule-coverage-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Database className="w-7 h-7 text-purple-600" />
                Rule Coverage Dashboard
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Canonical Rule Engine - Single Source of Truth
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-1" />
                New Rule
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="overview" className="data-[state=active]:bg-purple-100">
              <BarChart3 className="w-4 h-4 mr-1" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="rules" className="data-[state=active]:bg-purple-100">
              <BookOpen className="w-4 h-4 mr-1" />
              Rule Library
            </TabsTrigger>
            <TabsTrigger value="pathways" className="data-[state=active]:bg-purple-100">
              <GitBranch className="w-4 h-4 mr-1" />
              Pathway Rules
            </TabsTrigger>
            <TabsTrigger value="mappings" className="data-[state=active]:bg-purple-100">
              <Link2 className="w-4 h-4 mr-1" />
              VI Mappings
            </TabsTrigger>
            <TabsTrigger value="gaps" className="data-[state=active]:bg-purple-100">
              <AlertCircle className="w-4 h-4 mr-1" />
              Coverage Gaps
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Total Rules</CardDescription>
                    <CardTitle className="text-3xl">{stats.totalRules}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-slate-500">{stats.activeRules} active</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Pathway Rules</CardDescription>
                    <CardTitle className="text-3xl text-purple-600">{stats.pathwayRules}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-slate-500">Diagnostic gated flows</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>VI Mappings</CardDescription>
                    <CardTitle className="text-3xl">{stats.totalMappings}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-slate-500">{stats.uniqueVIItems} unique items</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardDescription>Coverage</CardDescription>
                    <CardTitle className="text-3xl text-green-600">{stats.coveragePercent}%</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Progress value={stats.coveragePercent} className="h-2" />
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Category Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-purple-600" />
                  Rules by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {Object.entries(groupedRules).map(([category, categoryRules]) => (
                    <div
                      key={category}
                      className={`p-3 rounded-lg border ${categoryColors[category] || categoryColors.other}`}
                    >
                      <div className="text-2xl font-bold">{categoryRules.length}</div>
                      <div className="text-xs font-medium capitalize">{category}</div>
                      <div className="text-xs opacity-70 mt-1">
                        {categoryRules.filter(r => r.is_pathway_rule).length} pathway
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-600" />
                  Pathway Rules Summary
                </CardTitle>
                <CardDescription>
                  Rules with investigation-first diagnostic flows
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {rules.filter(r => r.is_pathway_rule).map(rule => (
                    <div
                      key={rule.rule_id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-purple-50 border-purple-200"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-600 text-white flex items-center justify-center">
                          <GitBranch className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{rule.canonical_name}</p>
                          <p className="text-xs text-slate-500">
                            {rule.diagnostic_pathways?.length || 0} pathways • 
                            {rule.severity_keywords?.length || 0} severity triggers
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {rule.auto_tags?.map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            <Tag className="w-3 h-3 mr-1" />
                            {tag}
                          </Badge>
                        ))}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedRule(rule);
                            setActiveTab('pathways');
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {rules.filter(r => r.is_pathway_rule).length === 0 && (
                    <p className="text-center text-slate-500 py-4">No pathway rules configured</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rule Library Tab */}
          <TabsContent value="rules" className="space-y-4">
            {/* Search & Filter */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search rules by name, ID, or alias..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                      data-testid="rule-search-input"
                    />
                  </div>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="px-3 py-2 border rounded-md bg-white text-sm"
                    data-testid="category-filter-select"
                  >
                    <option value="all">All Categories</option>
                    <option value="pathway">Pathway Rules Only</option>
                    <option value="brakes">Brakes</option>
                    <option value="suspension">Suspension</option>
                    <option value="diagnostic">Diagnostic</option>
                    <option value="service">Service</option>
                    <option value="exhaust">Exhaust</option>
                    <option value="steering">Steering</option>
                    <option value="tyres">Tyres</option>
                    <option value="electrical">Electrical</option>
                    <option value="engine">Engine</option>
                    <option value="cooling">Cooling</option>
                  </select>
                </div>
              </CardContent>
            </Card>

            {/* Rules List */}
            <div className="space-y-2">
              {filteredRules.map(rule => (
                <RuleCard
                  key={rule.rule_id}
                  rule={rule}
                  categoryColors={categoryColors}
                  isSelected={selectedRule?.rule_id === rule.rule_id}
                  onSelect={() => setSelectedRule(selectedRule?.rule_id === rule.rule_id ? null : rule)}
                />
              ))}
              {filteredRules.length === 0 && (
                <Card>
                  <CardContent className="py-8 text-center text-slate-500">
                    No rules match your search criteria
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Pathway Rules Tab */}
          <TabsContent value="pathways" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GitBranch className="w-5 h-5 text-purple-600" />
                  Pathway-Based Rules
                </CardTitle>
                <CardDescription>
                  Rules with controlled diagnostic pathways (Investigation First vs Direct Repair)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {rules.filter(r => r.is_pathway_rule).map(rule => (
                    <PathwayRuleDetail key={rule.rule_id} rule={rule} />
                  ))}
                  {rules.filter(r => r.is_pathway_rule).length === 0 && (
                    <p className="text-center text-slate-500 py-8">
                      No pathway rules configured. Create your first pathway rule to enforce
                      investigation-first diagnostic flows.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* VI Mappings Tab */}
          <TabsContent value="mappings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-purple-600" />
                  VI Item → Rule Mappings
                </CardTitle>
                <CardDescription>
                  How inspection items map to canonical rules
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium">VI Item Key</th>
                        <th className="text-left px-4 py-3 font-medium">Status Condition</th>
                        <th className="text-left px-4 py-3 font-medium">Mapped Rule</th>
                        <th className="text-left px-4 py-3 font-medium">Rule Type</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {mappings.slice(0, 50).map((mapping, idx) => {
                        const rule = rules.find(r => r.rule_id === mapping.rule_id);
                        return (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="px-4 py-3 font-mono text-xs">{mapping.vi_item_key}</td>
                            <td className="px-4 py-3">
                              {mapping.status_condition ? (
                                <Badge variant="outline" className="text-xs">
                                  {mapping.status_condition}
                                </Badge>
                              ) : (
                                <span className="text-slate-400">Any</span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-medium">{rule?.canonical_name || mapping.rule_id}</span>
                            </td>
                            <td className="px-4 py-3">
                              {rule?.is_pathway_rule ? (
                                <Badge className="bg-purple-100 text-purple-800">Pathway</Badge>
                              ) : (
                                <Badge variant="secondary">{rule?.action_type || 'standard'}</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {mappings.length > 50 && (
                    <div className="px-4 py-3 bg-slate-50 text-center text-sm text-slate-500">
                      Showing 50 of {mappings.length} mappings
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Coverage Gaps Tab */}
          <TabsContent value="gaps" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  Coverage Gaps
                </CardTitle>
                <CardDescription>
                  VI items without mapped rules
                </CardDescription>
              </CardHeader>
              <CardContent>
                {coverage?.gaps?.length > 0 ? (
                  <div className="space-y-2">
                    {coverage.gaps.map((gap, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 rounded-lg border border-amber-200 bg-amber-50"
                      >
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                          <div>
                            <p className="font-medium text-slate-800">{gap.vi_item_key}</p>
                            <p className="text-xs text-slate-500">
                              {gap.occurrences} occurrences in inspections
                            </p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm">
                          <Plus className="w-4 h-4 mr-1" />
                          Create Rule
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="font-medium text-green-800">100% Coverage!</p>
                    <p className="text-sm text-slate-500">All VI items have mapped rules</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};


/**
 * Rule Card Component
 */
const RuleCard = ({ rule, categoryColors, isSelected, onSelect }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className={`cursor-pointer transition-all ${isSelected ? 'ring-2 ring-purple-500' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Category Badge */}
          <div className={`px-2 py-1 rounded text-xs font-medium capitalize ${categoryColors[rule.category] || categoryColors.other}`}>
            {rule.category}
          </div>

          {/* Rule Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-slate-800">{rule.canonical_name}</h4>
              {rule.is_pathway_rule && (
                <Badge className="bg-purple-600 text-white text-xs">
                  <GitBranch className="w-3 h-3 mr-1" />
                  Pathway
                </Badge>
              )}
              {rule.status !== 'active' && (
                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                  {rule.status}
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 font-mono mt-1">{rule.rule_id}</p>

            {/* Aliases */}
            {rule.aliases?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {rule.aliases.slice(0, 5).map((alias, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {alias}
                  </Badge>
                ))}
                {rule.aliases.length > 5 && (
                  <Badge variant="secondary" className="text-xs">
                    +{rule.aliases.length - 5} more
                  </Badge>
                )}
              </div>
            )}

            {/* Expanded Details */}
            {expanded && (
              <div className="mt-4 pt-4 border-t space-y-3">
                {/* Job Group Key */}
                <div>
                  <p className="text-xs font-medium text-slate-600">Job Group Key</p>
                  <p className="text-sm font-mono text-slate-800">{rule.job_group_key}</p>
                </div>

                {/* Pricing Templates */}
                {rule.pricing_templates?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-600 mb-1">Pricing Templates</p>
                    <div className="space-y-1">
                      {rule.pricing_templates.map((template, idx) => (
                        <div key={idx} className="text-xs bg-slate-50 p-2 rounded">
                          <span className="font-medium">{template.label}</span>
                          {template.default_hours && (
                            <span className="text-slate-500"> • {template.default_hours}h</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Relationships */}
                {rule.relationships?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-600 mb-1">Relationships</p>
                    <div className="flex flex-wrap gap-1">
                      {rule.relationships.map((rel, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {rel.type}: {rel.target_rule_id}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Job Guide */}
                {rule.job_guide?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-600 mb-1">Job Guide ({rule.job_guide.length} steps)</p>
                    <div className="text-xs text-slate-600 space-y-1">
                      {rule.job_guide.slice(0, 3).map((step, idx) => (
                        <p key={idx} className={step.is_warning ? 'text-amber-600' : ''}>
                          {step.order}. {step.text}
                        </p>
                      ))}
                      {rule.job_guide.length > 3 && (
                        <p className="text-slate-400">...and {rule.job_guide.length - 3} more steps</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
            >
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};


/**
 * Pathway Rule Detail Component
 */
const PathwayRuleDetail = ({ rule }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className="bg-purple-50 border-b border-purple-200 px-4 py-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-600 text-white flex items-center justify-center">
              <GitBranch className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-800">{rule.canonical_name}</h4>
              <p className="text-xs text-slate-500">
                {rule.diagnostic_pathways?.length || 0} pathways • 
                Data capture: {rule.data_capture_required ? 'Required' : 'Optional'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {rule.auto_tags?.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="text-xs border-purple-300 text-purple-700">
                {tag}
              </Badge>
            ))}
            {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-4 space-y-4">
          {/* Severity Keywords */}
          {rule.severity_keywords?.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                Severity Escalation Keywords
              </h5>
              <div className="flex flex-wrap gap-2">
                {rule.severity_keywords.map((keyword, idx) => (
                  <Badge key={idx} className="bg-red-100 text-red-800">
                    {keyword}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                When detected, rule escalates to HIGH RISK with catalyst damage advisory
              </p>
            </div>
          )}

          {/* Pathways */}
          <div>
            <h5 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
              <Layers className="w-4 h-4 text-purple-600" />
              Available Pathways
            </h5>
            <div className="space-y-3">
              {rule.diagnostic_pathways?.map((pathway, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    pathway.is_default 
                      ? 'border-blue-200 bg-blue-50' 
                      : 'border-green-200 bg-green-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{pathway.pathway_name}</span>
                      {pathway.is_default && (
                        <Badge className="bg-blue-600 text-white text-xs">Default</Badge>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mb-2">{pathway.description}</p>

                  {/* Evidence Required */}
                  {pathway.evidence_required?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-slate-500 mb-1">Gate Conditions:</p>
                      <div className="flex flex-wrap gap-1">
                        {pathway.evidence_required.map((req, ridx) => (
                          <Badge key={ridx} variant="outline" className="text-xs">
                            {req.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Workflow Steps */}
                  {pathway.internal_workflow?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-slate-500 mb-1">
                        Workflow ({pathway.internal_workflow.length} steps):
                      </p>
                      <div className="text-xs text-slate-600">
                        {pathway.internal_workflow.slice(0, 3).map((step, sidx) => (
                          <span key={sidx}>
                            {sidx > 0 && ' → '}
                            {step.text.substring(0, 30)}{step.text.length > 30 ? '...' : ''}
                          </span>
                        ))}
                        {pathway.internal_workflow.length > 3 && (
                          <span className="text-slate-400"> ...+{pathway.internal_workflow.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Pricing Templates */}
                  {pathway.pricing_templates?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-slate-500 mb-1">Auto-adds:</p>
                      <div className="flex flex-wrap gap-1">
                        {pathway.pricing_templates.map((template, tidx) => (
                          <Badge key={tidx} variant="secondary" className="text-xs">
                            {template.label}
                            {template.default_hours && ` (${template.default_hours}h)`}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Senior Tech Reminders */}
          {rule.senior_tech_reminders?.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-500" />
                Senior Technician Reminders
              </h5>
              <ul className="text-sm text-slate-600 space-y-1">
                {rule.senior_tech_reminders.map((reminder, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                    <span>{reminder}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


export default RuleCoverageDashboard;
